import React, { useState, useEffect } from 'react';
import { Customer, Guest } from '../../types/customer';
import { useTranslation } from 'react-i18next';
import pencilIcon from './icons/pencil.svg';
import deleteIcon from './icons/delete.svg';
import Button from '../Button';
import PassengerForm from '../PassengerForm';
import { updatePassengers } from '../../utils/functions';
import ModalWindow from '../ModalWindow';
import PassengerRemove from './PassengerRemove';

interface Props {
  incomingGuests: Guest[];
  hotellinxReservationLineId: string;
  reservationLineId: string | undefined;
  customerData: Customer;
  numAdults: number;
  idToken: string;
  isPassenger: boolean;
  isFutureReservation: boolean;
  resort: string;
}

const FellowPassengers: React.FC<Props> = ({
  incomingGuests,
  hotellinxReservationLineId,
  reservationLineId,
  customerData,
  numAdults,
  idToken,
  isPassenger,
  isFutureReservation,
  resort,
}) => {
  const { t } = useTranslation(['bookings', 'common']);

  // Determine if there are incoming guests
  const hasGuests = incomingGuests && incomingGuests.length > 0;

  // Set the main contact from incomingGuests if present,
  // otherwise fall back to customerData.
  const mainCustomer = incomingGuests.find(
    (guest) => guest.Email === customerData.email
  ) || {
    FirstName: customerData.givenName,
    Surname: customerData.surname,
    Email: customerData.email,
    Mobile: customerData.mobilePhone,
    MainCustomer: customerData.email,
  };

  // Add the MainCustomer email to all the guests objects
  const incomingGuestsWithMainCustomer = (incomingGuests ?? []).map(
    (guest) => ({
      ...guest,
      MainCustomer: customerData.email,
    })
  );

  const [canAddMorePassengers, setCanAddMorePassengers] = useState(
    incomingGuestsWithMainCustomer.length < numAdults
  );

  const [isAdding, setIsAdding] = useState(false);
  const [passengerToEdit, setPassengerToEdit] = useState<string | null>(null);
  const [passengerToRemove, setPassengerToRemove] = useState<string | null>(
    null
  );
  const [guests, setGuests] = useState(incomingGuestsWithMainCustomer ?? []);
  const [modalIsOpen, setModalIsOpen] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleEdit = (email: string) => {
    setPassengerToEdit(email);
    setIsAdding(true);
  };

  const handleCancel = () => {
    setPassengerToEdit(null);
    setIsAdding(false);
  };

  const confirmRemove = async (email: string) => {
    setIsLoading(true);

    const updatedGuests = guests.map((guest) => {
      if (guest.Email === email) {
        guest.removed = true;
      }

      return guest;
    });

    await updatePassengers(
      resort,
      updatedGuests,
      hotellinxReservationLineId,
      reservationLineId,
      idToken
    );

    setGuests(updatedGuests.filter((guest) => !guest.removed));

    setPassengerToRemove(null);
    setIsLoading(false);
  };

  const cancelRemove = () => {
    setPassengerToRemove(null);
  };

  function filterGuests(guest: Guest) {
    return guest.Email !== guest.MainCustomer;
  }

  const handleSubmit = async (updatedGuest: Guest) => {
    setIsLoading(true);
    const updatedGuests = [...guests];

    try {
      // When the passenger's data is edited
      if (passengerToEdit !== null) {
        const index = guests.findIndex(
          (guest) => guest.Email === passengerToEdit
        );
        updatedGuests[index] = updatedGuest;
      } else {
        // When a new passenger is being added
        // Ensure the new guest is not the main contact (use the same email)
        if (updatedGuest.Email !== mainCustomer.Email) {
          updatedGuests.push(updatedGuest);
        }
      }

      // Ensure the main contact is always in the guests array
      if (!updatedGuests.some((guest) => guest.Email === mainCustomer.Email)) {
        updatedGuests.push(mainCustomer);
      }

      const result = await updatePassengers(
        resort,
        updatedGuests,
        hotellinxReservationLineId,
        reservationLineId,
        idToken
      );

      if (result.status === "200") {
        setGuests(updatedGuests);
        setPassengerToEdit(null);
        setIsAdding(false);
      } else {
        setErrorMessage(t(`${result.errorMessage}`) || null);
        setModalIsOpen(true);
      }
    } catch (error) {
      setErrorMessage(
        error instanceof Error ? error.message : 'An unexpected error occurred.'
      );
      setModalIsOpen(true);
    }

    setIsLoading(false);
  };

  useEffect(() => {
    setCanAddMorePassengers(guests.length < numAdults);
  }, [guests]);

  return (
    <div className='bg-white'>
      <p className='text-2xl md:text-3xl mb-4'>{t('Passengers')}</p>
      <p className='mb-4'>
        {t('Booking details will be sent to co-travelers by email.')}
      </p>
      <div key={0} className={`px-4 mb-4 flex justify-between items-start`}>
        <div>
          <label className='font-rukaNeue text-xl block mb-2'>
            {t('Main Contact')}
          </label>
          <p className='mb-1'>{`${mainCustomer.FirstName} ${mainCustomer.Surname}`}</p>
          <p className='mb-1'>{mainCustomer.Email}</p>
          <p className='mb-4'>{mainCustomer.Mobile}</p>
        </div>
      </div>

      {hasGuests && (
        <>
          {guests.filter(filterGuests).map((guest, index) => (
            <div
              key={index}
              className={`p-4 ${
                index % 2 === 1 ? 'bg-white' : 'bg-gray-200'
              } flex justify-between items-start flex-col md:flex-row`}
            >
              {passengerToRemove === guest.Email && (
                <PassengerRemove
                  index={index + 1}
                  isLoading={isLoading}
                  guest={guest}
                  confirmRemove={confirmRemove}
                  cancelRemove={cancelRemove}
                />
              )}

              {passengerToEdit === guest.Email && (
                <PassengerForm
                  index={index + 1}
                  mainCustomer={customerData.email}
                  onSubmit={(updatedGuest: Guest) => handleSubmit(updatedGuest)}
                  onCancel={handleCancel}
                  guest={guest}
                  isLoading={isLoading}
                />
              )}

              {passengerToEdit !== guest.Email &&
                passengerToRemove !== guest.Email && (
                  <div>
                    <label className='font-rukaNeue text-xl block mb-2'>
                      {`${t('Traveler')} ${index + 1}`}
                    </label>
                    <p className='mb-1'>{`${guest.FirstName} ${guest.Surname}`}</p>
                    <p className='mb-1'>{guest.Email}</p>
                    <p className='mb-1'>{guest.Mobile}</p>
                  </div>
                )}

              {passengerToEdit !== guest.Email &&
                passengerToRemove !== guest.Email && (
                  <div className='font-rukaNeue flex flex-col md:items-end w-full'>
                    <>
                      <Button
                        className='mb-2 text-black border border-black'
                        variant='transparent'
                        icon={pencilIcon}
                        onClick={() => handleEdit(guest.Email)}
                      >
                        {t('Edit')}
                      </Button>
                      <Button
                        className='text-black border border-black'
                        variant='transparent'
                        icon={deleteIcon}
                        onClick={() => setPassengerToRemove(guest.Email)}
                      >
                        {t('Remove')}
                      </Button>
                    </>
                  </div>
                )}
            </div>
          ))}
        </>
      )}

      <div
        className={`mt-4 text-left p-4 ${
          isAdding && !passengerToEdit ? 'bg-gray-200' : ''
        }`}
      >
        {isAdding && passengerToEdit === null && (
          <PassengerForm
            index={guests ? guests.length : 0}
            mainCustomer={customerData.email}
            onSubmit={(addedGuest: Guest) => handleSubmit(addedGuest)}
            onCancel={handleCancel}
            isLoading={isLoading}
          />
        )}
        {!isPassenger &&
          isFutureReservation &&
          canAddMorePassengers &&
          !isAdding && (
            <Button
              className='w-full md:w-auto text-black border border-black'
              variant='transparent'
              onClick={() => setIsAdding(true)}
            >
              {t('Add Passenger') ?? ''}
            </Button>
          )}
      </div>
      <ModalWindow
        isOpen={modalIsOpen}
        setIsOpen={setModalIsOpen}
        title='Error'
      >
        <div className='py-4'>
          {errorMessage && (<p style={{ marginBottom: "15px" }}>{errorMessage}</p>)}
          <Button onClick={() => { 
            setModalIsOpen(false); 
            setErrorMessage(null);
          }}>{t('Close')}</Button>
        </div>
      </ModalWindow>
    </div>
  );
};

export default FellowPassengers;
